/**
 * INV-RC-17 — ONE client-side signal for "Palate is temporarily unavailable".
 *
 * WHY (ruling 2026-09-30, "a 503 is not an authentication failure"): while the
 * API's session store (Redis) is down, every signed-in call answers
 * `503 SESSION_STORE_UNAVAILABLE` with a retry hint (backend#1969) — the
 * server's way of saying "your session was NOT rejected, ask again shortly".
 * A gateway 502/504, or no answer at all (offline, DNS, a proxy error the
 * browser hides behind CORS), is the same kind of event. None of them may end
 * the session. Instead:
 *   - the tokens are kept and nothing redirects (axios.config.ts);
 *   - the user sees one non-blocking "Service temporarily unavailable —
 *     retrying" banner (components/shared/service-unavailable-banner.tsx);
 *   - a single lightweight authenticated probe asks again on the server's own
 *     `Retry-After`, backing off while it has none;
 *   - the first authenticated answer ends the outage and re-runs the reads that
 *     failed during it (query.config.ts), so the app resumes on its own.
 *
 * Mirrors the degraded state palate-pos-app#412 added for the same outage
 * (`src/lib/heartbeat-status.ts`), so the clients read it the same way:
 *   - Only AUTHENTICATED traffic records or clears it. A public call proves
 *     nothing about the session store.
 *   - Answers are ordered by SEND order (`issueRequestSeq`), never wall-clock:
 *     a 503 to a request sent before the one that proved recovery cannot
 *     reopen the outage, and a success to a request sent before the outage was
 *     recorded cannot end it.
 *   - A feature-level 503 — the API's own error envelope carrying neither an
 *     outage code nor a retry hint (payment processing or menu capture not
 *     available) — is NOT an outage: the caller shows its message and the
 *     rest of the app is fine.
 */

import { useSyncExternalStore } from "react";

// ---------------------------------------------------------------------------
// Classification
// ---------------------------------------------------------------------------

/** Codes the backend sends when a dependency outage, not a feature, refused the call. */
export const SERVICE_OUTAGE_CODES: ReadonlySet<string> = new Set([
  "SESSION_STORE_UNAVAILABLE",
  "SERVICE_TEMPORARILY_UNAVAILABLE",
  "SERVICE_UNAVAILABLE",
]);

export type ServiceOutageKind =
  /** The API, or the gateway in front of it, answered 502/503/504. */
  | "server"
  /** Nothing answered while the browser reports it is online. */
  | "unreachable"
  /** The browser reports it is offline. */
  | "offline";

export interface ServiceOutage {
  kind: ServiceOutageKind;
  /** HTTP status, or null when nothing answered. */
  status: number | null;
  /** The server's machine code (`details.code`), when it sent one. */
  code: string | null;
  /** The server's own explanation, when it sent one. */
  message: string | null;
  /** How long the server asked the client to wait before asking again, in ms. */
  retryAfterMs: number | null;
}

type ResponseLike = { status?: unknown; data?: unknown; headers?: unknown };
type ErrorLike = {
  code?: unknown;
  name?: unknown;
  message?: unknown;
  isAxiosError?: unknown;
  response?: ResponseLike;
  originalError?: unknown;
};

/** Axios codes that mean "no answer": offline, DNS, a dropped connection, a timeout. */
const NO_ANSWER_CODES = new Set(["ERR_NETWORK", "ECONNABORTED", "ETIMEDOUT"]);

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * The axios error behind `error`. Service calls throw through `handleApiError`
 * (app/lib/utils.ts), which wraps the axios error as `originalError` — the
 * response, and its headers, live on the original.
 */
function underlyingError(error: unknown): ErrorLike | null {
  let current = asRecord(error) as ErrorLike | null;
  // Bounded: a wrapper chain is one or two deep; never spin on a cycle.
  for (let depth = 0; current && depth < 5; depth += 1) {
    if (current.response || current.isAxiosError === true) return current;
    const inner = asRecord(current.originalError) as ErrorLike | null;
    if (!inner) return current;
    current = inner;
  }
  return current;
}

function readHeader(headers: unknown, name: string): unknown {
  const record = asRecord(headers);
  if (!record) return undefined;
  const get = (record as { get?: unknown }).get;
  if (typeof get === "function") {
    const value: unknown = get.call(record, name);
    if (value !== undefined && value !== null) return value;
  }
  const wanted = name.toLowerCase();
  const key = Object.keys(record).find((k) => k.toLowerCase() === wanted);
  return key === undefined ? undefined : record[key];
}

/** A `Retry-After` value (delta-seconds or an HTTP-date) in ms; null when unusable. */
function parseRetryAfter(raw: unknown, now: number): number | null {
  if (typeof raw === "number") {
    return Number.isFinite(raw) && raw > 0 ? raw * 1000 : null;
  }
  if (typeof raw !== "string" || raw.trim() === "") return null;
  const value = raw.trim();
  if (/^\d+$/.test(value)) {
    const ms = Number(value) * 1000;
    return ms > 0 ? ms : null;
  }
  const at = Date.parse(value);
  if (Number.isNaN(at)) return null;
  const ms = at - now;
  return ms > 0 ? ms : null;
}

/**
 * The server's retry hint, in ms, or null when it sent none.
 *
 * The `Retry-After` header wins. The body's `retryAfter` (seconds, under
 * `details` in the API's error envelope) is the fallback that matters in a
 * browser: cross-origin, only CORS-safelisted response headers are readable,
 * and the API does not list `Retry-After` in `Access-Control-Expose-Headers`,
 * so the header this app receives is usually invisible to it.
 */
export function retryAfterMsOf(
  error: unknown,
  now: number = Date.now(),
): number | null {
  const response = underlyingError(error)?.response;
  if (!response) return null;
  const fromHeader = parseRetryAfter(readHeader(response.headers, "retry-after"), now);
  if (fromHeader !== null) return fromHeader;
  const body = asRecord(response.data);
  const details = asRecord(body?.details);
  return (
    parseRetryAfter(details?.retryAfter, now) ??
    parseRetryAfter(body?.retryAfter, now)
  );
}

function isBrowserOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

/**
 * Whether `error` means the service is temporarily unavailable — and if so,
 * what the server said about it. Null for everything else: an auth failure,
 * a validation error, a bug, a request this app cancelled itself.
 */
export function classifyServiceOutage(error: unknown): ServiceOutage | null {
  const err = underlyingError(error);
  if (!err) return null;
  if (err.code === "ERR_CANCELED" || err.name === "CanceledError") return null;

  const response = err.response;
  if (!response || typeof response.status !== "number") {
    const noAnswer =
      (typeof err.code === "string" && NO_ANSWER_CODES.has(err.code)) ||
      err.message === "Network Error";
    if (!noAnswer) return null;
    return {
      kind: isBrowserOffline() ? "offline" : "unreachable",
      status: null,
      code: null,
      message: null,
      retryAfterMs: null,
    };
  }

  const status = response.status;
  if (status !== 502 && status !== 503 && status !== 504) return null;

  const body = asRecord(response.data);
  const details = asRecord(body?.details);
  const rawCode = details?.code ?? body?.code;
  const code = typeof rawCode === "string" ? rawCode : null;
  const message =
    typeof body?.message === "string" && body.message.trim() !== ""
      ? body.message
      : null;
  const retryAfterMs = retryAfterMsOf(err);

  if (status === 503) {
    // The API's own envelope with its own reason and no retry hint is a
    // feature saying "not available right now" — not the service going away.
    // A proxy's 503 (HTML, or empty) carries no envelope and still counts.
    const isApiEnvelope =
      body !== null &&
      (typeof body.message === "string" ||
        typeof body.error === "string" ||
        typeof body.statusCode === "number");
    const namedOutage = code !== null && SERVICE_OUTAGE_CODES.has(code);
    if (isApiEnvelope && !namedOutage && retryAfterMs === null) return null;
  }

  return { kind: "server", status, code, message, retryAfterMs };
}

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export interface ServiceAvailability {
  /** True while the API answers "temporarily unavailable", or does not answer. */
  unavailable: boolean;
  /** When the current outage was first recorded (ms epoch), or null. */
  since: number | null;
  /** The latest outage answer, or null while the API serves. */
  outage: ServiceOutage | null;
}

const AVAILABLE: ServiceAvailability = Object.freeze({
  unavailable: false,
  since: null,
  outage: null,
});

let snapshot: ServiceAvailability = AVAILABLE;
const listeners = new Set<() => void>();
const recoveryListeners = new Set<() => void>();

/** Send-order counter: every authenticated dispatch draws the next number. */
let lastIssuedSeq = 0;
/** `lastIssuedSeq` when the current outage was recorded. */
let unavailableAtSeq: number | null = null;
/** Send order of the newest request whose answer proved the API serves. */
let newestServedSeq = 0;

function publish(next: ServiceAvailability): void {
  snapshot = next;
  listeners.forEach((listener) => listener());
}

/** The send-order number for one dispatch — see "answers are ordered by SEND order". */
export function issueRequestSeq(): number {
  lastIssuedSeq += 1;
  return lastIssuedSeq;
}

interface AnswerOrder {
  /** `issueRequestSeq()` drawn when the answered request was sent. */
  sentSeq?: number;
}

/**
 * Records an outage answer. Opens the outage (and arms the recovery probe) on
 * the first; later answers only refresh what the banner and probe read.
 */
export function recordServiceUnavailable(
  outage: ServiceOutage,
  { sentSeq, now = Date.now() }: AnswerOrder & { now?: number } = {},
): void {
  // Sent before a request whose answer already proved the API serves: stale.
  if (sentSeq !== undefined && sentSeq <= newestServedSeq) return;

  if (!snapshot.unavailable) {
    unavailableAtSeq = lastIssuedSeq;
    consecutiveProbeFailures = 0;
    publish({ unavailable: true, since: now, outage });
    scheduleProbe();
    return;
  }
  const previous = snapshot.outage;
  if (
    previous?.kind !== outage.kind ||
    previous.code !== outage.code ||
    previous.message !== outage.message ||
    previous.retryAfterMs !== outage.retryAfterMs
  ) {
    publish({ ...snapshot, outage });
  }
}

/**
 * Records proof that the API serves — an authenticated success. Ends the
 * outage and notifies the recovery listeners, unless the request was sent
 * before the outage was recorded (its success says nothing about now).
 */
export function recordServiceAvailable({ sentSeq }: AnswerOrder = {}): void {
  if (!snapshot.unavailable) {
    if (sentSeq !== undefined && sentSeq > newestServedSeq) {
      newestServedSeq = sentSeq;
    }
    return;
  }
  if (
    sentSeq !== undefined &&
    unavailableAtSeq !== null &&
    sentSeq <= unavailableAtSeq
  ) {
    return;
  }
  newestServedSeq = Math.max(newestServedSeq, sentSeq ?? lastIssuedSeq);
  unavailableAtSeq = null;
  consecutiveProbeFailures = 0;
  cancelProbe();
  publish(AVAILABLE);
  recoveryListeners.forEach((listener) => {
    try {
      listener();
    } catch {
      // A listener's failure must never keep the app in its outage state.
    }
  });
}

/**
 * Forgets any outage without announcing a recovery — for sign-out, where
 * there is no longer a session to keep alive. Anything still on the wire
 * belongs to the session that ended, so its answers are ignored.
 */
export function resetServiceAvailability(): void {
  cancelProbe();
  consecutiveProbeFailures = 0;
  unavailableAtSeq = null;
  newestServedSeq = lastIssuedSeq;
  if (snapshot !== AVAILABLE) publish(AVAILABLE);
}

export function getServiceAvailability(): ServiceAvailability {
  return snapshot;
}

export function isServiceUnavailable(): boolean {
  return snapshot.unavailable;
}

export function subscribeServiceAvailability(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Runs `listener` each time an outage ends (not on sign-out). */
export function onServiceRecovered(listener: () => void): () => void {
  recoveryListeners.add(listener);
  return () => {
    recoveryListeners.delete(listener);
  };
}

/** Re-renders on every outage transition (and when its reason changes). */
export function useServiceAvailability(): ServiceAvailability {
  return useSyncExternalStore(
    subscribeServiceAvailability,
    getServiceAvailability,
    getServiceAvailability,
  );
}

// ---------------------------------------------------------------------------
// Recovery probe
// ---------------------------------------------------------------------------

/** Floor on the probe delay: a recovering API is never hammered. */
export const PROBE_MIN_DELAY_MS = 2_000;
/** Ceiling of the probe backoff while the server gives no hint. */
export const PROBE_MAX_DELAY_MS = 15_000;
/** A server hint is honoured up to this, so a wild value can't park the app. */
export const PROBE_RETRY_AFTER_CAP_MS = 60_000;

/**
 * Delay before the next probe: exponential from PROBE_MIN_DELAY_MS to
 * PROBE_MAX_DELAY_MS, never sooner than the server's `Retry-After` (capped at
 * PROBE_RETRY_AFTER_CAP_MS), plus up to 20% jitter so every open tab in a
 * restaurant doesn't ask in the same instant.
 */
export function nextProbeDelayMs(
  failures: number,
  retryAfterMs: number | null,
  random: () => number = Math.random,
): number {
  const backoff = Math.min(
    PROBE_MIN_DELAY_MS * 2 ** Math.max(0, failures),
    PROBE_MAX_DELAY_MS,
  );
  const asked =
    retryAfterMs === null ? 0 : Math.min(retryAfterMs, PROBE_RETRY_AFTER_CAP_MS);
  return Math.round(Math.max(backoff, asked) * (1 + random() * 0.2));
}

export interface ServiceAvailabilityProbe {
  /**
   * Sends one lightweight AUTHENTICATED request. Its answer is recorded by the
   * HTTP client like any other: a success ends the outage.
   */
  probe: () => Promise<unknown>;
  /** False once there is no session left to keep — the outage is then forgotten. */
  canProbe: () => boolean;
}

let probeConfig: ServiceAvailabilityProbe | null = null;
let probeTimer: ReturnType<typeof setTimeout> | null = null;
let probeInFlight = false;
let consecutiveProbeFailures = 0;
let onlineListenerAttached = false;

function cancelProbe(): void {
  if (probeTimer !== null) {
    clearTimeout(probeTimer);
    probeTimer = null;
  }
}

function scheduleProbe(): void {
  if (!snapshot.unavailable || !probeConfig) return;
  if (probeTimer !== null || probeInFlight) return;
  const delay = nextProbeDelayMs(
    consecutiveProbeFailures,
    snapshot.outage?.retryAfterMs ?? null,
  );
  probeTimer = setTimeout(() => {
    probeTimer = null;
    void runProbe();
  }, delay);
}

async function runProbe(): Promise<void> {
  if (!snapshot.unavailable || !probeConfig || probeInFlight) return;
  if (!probeConfig.canProbe()) {
    resetServiceAvailability();
    return;
  }
  probeInFlight = true;
  try {
    await probeConfig.probe();
  } catch (error) {
    // The API answered with something other than "unavailable" (a 401 on a
    // support-mode token, a 500): it IS answering, so the outage is over and
    // the app's normal error handling takes it from here.
    if (!classifyServiceOutage(error)) recordServiceAvailable();
  } finally {
    probeInFlight = false;
  }
  if (snapshot.unavailable) {
    consecutiveProbeFailures += 1;
    scheduleProbe();
  }
}

/** Probe now instead of at the next scheduled time (the browser came back online). */
export function probeServiceNow(): void {
  if (!snapshot.unavailable || probeInFlight) return;
  cancelProbe();
  void runProbe();
}

/**
 * Wires the probe the HTTP client sends (axios.config.ts). Idempotent. Also
 * probes the moment the browser reports it is back online.
 */
export function configureServiceAvailabilityProbe(
  config: ServiceAvailabilityProbe | null,
): void {
  probeConfig = config;
  if (!config) {
    cancelProbe();
    return;
  }
  if (!onlineListenerAttached && typeof window !== "undefined") {
    onlineListenerAttached = true;
    window.addEventListener("online", probeServiceNow);
  }
  scheduleProbe();
}
